/**
 * Сборка и выдача наборов данных.
 *
 * Частота выводится из ранга по закону Ципфа: частота обратно пропорциональна
 * рангу. Это даёт правдоподобное распределение без хранения абсолютных
 * счётчиков, которые пришлось бы лицензировать вместе с корпусом.
 */

import type { LanguageCode } from '@type2x/shared-contracts';

import { EN_WORD_LIST } from './en-words.js';
import { RU_WORD_LIST } from './ru-words.js';
import { EN_SENTENCES } from './en-sentences.js';
import { RU_SENTENCES } from './ru-sentences.js';
import { EN_SENTENCE_SOURCE, EN_SOURCE, RU_SENTENCE_SOURCE, RU_SOURCE } from './sources.js';
import type { CorpusSet, CorpusSource, SentenceEntry, WordEntry } from './types.js';

/** Минимальная длина начала слова, по которому идёт поиск предложения. */
const MIN_STEM_LENGTH = 4;

/** Какая доля слова считается его неизменяемым началом. */
const STEM_RATIO = 0.7;

/** Опции отбора слов. */
export interface WordQuery {
  /** Только слова, полностью составленные из этих символов. */
  readonly allowedCharacters?: ReadonlySet<string>;
  /** Минимальная длина слова. */
  readonly minLength?: number;
  /** Максимальная длина слова. */
  readonly maxLength?: number;
  /** Ограничение количества. */
  readonly limit?: number;
}

function buildWords(list: readonly string[], language: LanguageCode): WordEntry[] {
  const seen = new Set<string>();
  const entries: WordEntry[] = [];

  for (const text of list) {
    // Дубли в списке возможны при ручном редактировании: первый ранг главнее.
    if (seen.has(text)) continue;
    seen.add(text);

    const rank = entries.length + 1;
    entries.push({
      text,
      language,
      rank,
      frequency: 1 / rank,
      characters: new Set([...text.toLowerCase()]),
    });
  }

  const maxFrequency = entries[0]?.frequency ?? 1;
  return entries.map((entry) => ({ ...entry, frequency: entry.frequency / maxFrequency }));
}

const SETS: Readonly<Record<LanguageCode, CorpusSet>> = {
  ru: {
    language: 'ru',
    source: RU_SOURCE,
    sentenceSource: RU_SENTENCE_SOURCE,
    words: buildWords(RU_WORD_LIST, 'ru'),
    sentences: RU_SENTENCES,
  },
  en: {
    language: 'en',
    source: EN_SOURCE,
    sentenceSource: EN_SENTENCE_SOURCE,
    words: buildWords(EN_WORD_LIST, 'en'),
    sentences: EN_SENTENCES,
  },
};

/** Полный набор данных языка. */
export function getCorpus(language: LanguageCode): CorpusSet {
  return SETS[language];
}

/** Слова языка с необязательной фильтрацией. */
export function getWords(language: LanguageCode, query: WordQuery = {}): readonly WordEntry[] {
  const { allowedCharacters, minLength = 1, maxLength = Number.MAX_SAFE_INTEGER, limit } = query;

  let result = SETS[language].words.filter((word) => {
    const length = [...word.text].length;
    if (length < minLength || length > maxLength) return false;
    if (allowedCharacters === undefined) return true;
    return [...word.characters].every((character) => allowedCharacters.has(character));
  });

  if (limit !== undefined) result = result.slice(0, limit);
  return result;
}

/** Предложения языка, при необходимости содержащие заданные слова. */
export function getSentences(
  language: LanguageCode,
  focusWords: readonly string[] = [],
): readonly SentenceEntry[] {
  const sentences = SETS[language].sentences;
  if (focusWords.length === 0) return sentences;

  const wanted = new Set(focusWords);
  return sentences.filter((sentence) => sentence.focusWords.some((word) => wanted.has(word)));
}

/**
 * Предложения, в которых встречается слово.
 *
 * Сначала ищется явная пометка: словарь помечает предложение начальной формой
 * слова. Если пометки нет, слово ищется в тексте по неизменяемому началу —
 * «работа» находится в «работаю». Выдумывать предложение нельзя: машинная
 * конструкция не тренирует реальный набор (ТЗ §4.6, §8.3), поэтому для слова
 * без подходящей фразы возвращается пустой список.
 */
export function sentencesForWord(language: LanguageCode, word: string): readonly SentenceEntry[] {
  const tagged = getSentences(language, [word]);
  if (tagged.length > 0) return tagged;

  const stem = stemOf(word);
  if (stem === null) return [];

  return SETS[language].sentences.filter((sentence) => sentence.text.toLowerCase().includes(stem));
}

/**
 * Неизменяемое начало слова либо null, если слово для поиска слишком короткое.
 *
 * Короткое начало вроде «и» нашлось бы в любом предложении и подобрало бы
 * фразу, к слову не относящуюся.
 */
function stemOf(word: string): string | null {
  const characters = [...word.toLowerCase()];
  if (characters.length < MIN_STEM_LENGTH) return null;

  const length = Math.max(MIN_STEM_LENGTH, Math.ceil(characters.length * STEM_RATIO));
  return characters.slice(0, length).join('');
}

/** Метаданные источника словаря языка: название, лицензия, версия. */
export function getSource(language: LanguageCode): CorpusSource {
  return SETS[language].source;
}

/** Метаданные источника предложений: у них своя лицензия и своя версия. */
export function getSentenceSource(language: LanguageCode): CorpusSource {
  return SETS[language].sentenceSource;
}
